import { MapUtils } from '../../utility/map-utils.js';
import { DayOfWeek } from './day-of-week.js';
import { DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { WeeklyScheduleDay } from './weekly-schedule-day.js';

export class WeeklySchedule extends DataModel {
  static readonly TYPE_ID: string = 'c3c0a7e5a6d844b09fbcea158a3e0139';

  constructor(
    public readonly monday: WeeklyScheduleDay = WeeklyScheduleDay.nineToFive(DayOfWeek.Monday),
    public readonly tuesday: WeeklyScheduleDay = WeeklyScheduleDay.nineToFive(DayOfWeek.Tuesday),
    public readonly wednesday: WeeklyScheduleDay = WeeklyScheduleDay.nineToFive(DayOfWeek.Wednesday),
    public readonly thursday: WeeklyScheduleDay = WeeklyScheduleDay.nineToFive(DayOfWeek.Thursday),
    public readonly friday: WeeklyScheduleDay = WeeklyScheduleDay.nineToFive(DayOfWeek.Friday),
    public readonly saturday: WeeklyScheduleDay = WeeklyScheduleDay.closed(DayOfWeek.Saturday),
    public readonly sunday: WeeklyScheduleDay = WeeklyScheduleDay.closed(DayOfWeek.Sunday),
  ) {
    super();
  }

  get $typeId(): string {
    return WeeklySchedule.TYPE_ID;
  }

  get hasAnyHoursDefined(): boolean {
    return this.monday.hasAnyHoursDefined ||
        this.tuesday.hasAnyHoursDefined ||
        this.wednesday.hasAnyHoursDefined ||
        this.thursday.hasAnyHoursDefined ||
        this.friday.hasAnyHoursDefined ||
        this.saturday.hasAnyHoursDefined ||
        this.sunday.hasAnyHoursDefined;
  }

  static fromMap(context: SerializationContext, map: SerializedData): WeeklySchedule {
    return new WeeklySchedule(
        context.fromData<WeeklyScheduleDay>(MapUtils.getRaw(map, 'monday') as SerializedData),
        context.fromData<WeeklyScheduleDay>(MapUtils.getRaw(map, 'tuesday') as SerializedData),
        context.fromData<WeeklyScheduleDay>(MapUtils.getRaw(map, 'wednesday') as SerializedData),
        context.fromData<WeeklyScheduleDay>(MapUtils.getRaw(map, 'thursday') as SerializedData),
        context.fromData<WeeklyScheduleDay>(MapUtils.getRaw(map, 'friday') as SerializedData),
        context.fromData<WeeklyScheduleDay>(MapUtils.getRaw(map, 'saturday') as SerializedData),
        context.fromData<WeeklyScheduleDay>(MapUtils.getRaw(map, 'sunday') as SerializedData),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'monday': context.toData(this.monday),
      'tuesday': context.toData(this.tuesday),
      'wednesday': context.toData(this.wednesday),
      'thursday': context.toData(this.thursday),
      'friday': context.toData(this.friday),
      'saturday': context.toData(this.saturday),
      'sunday': context.toData(this.sunday),
    };
  }

  clone(): this {
    return new WeeklySchedule(
        this.monday.clone(),
        this.tuesday.clone(),
        this.wednesday.clone(),
        this.thursday.clone(),
        this.friday.clone(),
        this.saturday.clone(),
        this.sunday.clone(),
    ) as this;
  }

  isSame(other: WeeklySchedule): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.monday, this.monday) &&
        isSameData(other.tuesday, this.tuesday) &&
        isSameData(other.wednesday, this.wednesday) &&
        isSameData(other.thursday, this.thursday) &&
        isSameData(other.friday, this.friday) &&
        isSameData(other.saturday, this.saturday) &&
        isSameData(other.sunday, this.sunday);
  }

  copyWithDay(day: WeeklyScheduleDay): WeeklySchedule {
    return new WeeklySchedule(
      day.dayOfWeek === DayOfWeek.Monday ? day : this.monday,
      day.dayOfWeek === DayOfWeek.Tuesday ? day : this.tuesday,
      day.dayOfWeek === DayOfWeek.Wednesday ? day : this.wednesday,
      day.dayOfWeek === DayOfWeek.Thursday ? day : this.thursday,
      day.dayOfWeek === DayOfWeek.Friday ? day : this.friday,
      day.dayOfWeek === DayOfWeek.Saturday ? day : this.saturday,
      day.dayOfWeek === DayOfWeek.Sunday ? day : this.sunday,
    );
  }

  atDayOfWeek(dayOfWeek: DayOfWeek): WeeklyScheduleDay {
    switch(dayOfWeek) {
      case DayOfWeek.Monday:
        return this.monday;
      case DayOfWeek.Tuesday:
        return this.tuesday;
      case DayOfWeek.Wednesday:
        return this.wednesday;
      case DayOfWeek.Thursday:
        return this.thursday;
      case DayOfWeek.Friday:
        return this.friday;
      case DayOfWeek.Saturday:
        return this.saturday;
      case DayOfWeek.Sunday:
        return this.sunday;
    }
  }
}

export class WeeklyScheduleBuilder extends DataModelBuilder<WeeklySchedule> {
  build(context: SerializationContext, map: SerializedData): WeeklySchedule {
    return WeeklySchedule.fromMap(context, map);
  }
}
